/**
 * @file Hook orchestrant le flow de soumission de réservation côté widget public.
 * @module packages.common.components.table-reservation.hooks.useReservationHandlers
 *
 * Centralise la gestion des transitions entre étapes (paiement, waitlist, soumission)
 * et la confirmation Stripe (PaymentIntent ou SetupIntent selon le mode). Découple la
 * logique de soumission du composant principal pour rester testable et agnostique de
 * l'hôte (widget embarqué ou page publique).
 */
import {useState, useCallback} from 'react';
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {getStripePromise} from 'packages/common/utils/stripeLoader';
import {buildReservationDateTime} from '../utils/buildReservationDateTime';
import {PAYMENT_ERROR_STAGES} from '../utils/paymentSignal';
import {confirmCardHold, buildCardHoldPayload} from '../utils/confirmCardHold';
import {isSlotLostCode} from '../constants/reservation';

/**
 * Champs du formulaire remis à zéro quand le client repart du calendrier :
 * le créneau (date, heure, données du créneau, salle du jour) et l'empreinte
 * (intent consommé ou obsolète). Les formules et les indicateurs de liste
 * d'attente se recalculent seuls au prochain choix.
 */
const SLOT_RESET_FIELDS = [
  'date',
  'timeSlot',
  'selectedTimeSlotData',
  'selectedAreaId',
  'paymentMethodId',
  'paymentIntentId',
  'paymentConfig',
  'confirmedIntent',
  'slotLost',
];

/**
 * Centralise les handlers de navigation, soumission et gestion d'erreur du widget.
 *
 * @param {Object} params - Paramètres du hook
 * @param {Object} params.formData - Données du formulaire de réservation
 * @param {Object} params.config - Configuration du restaurant (durée par défaut, etc.)
 * @param {Object} params.STEPS - Mapping des étapes logiques (BASE_STEPS)
 * @param {boolean} params.isLastStep - Indique si l'étape courante est la dernière avant confirmation
 * @param {Function} params.updateFormData - Écrit un champ dans le formData (statut réel renvoyé par le backend)
 * @param {Function} params.goToStep - Navigation vers une étape spécifique
 * @param {Function} params.rewindToStep - Retour à une étape en invalidant les suivantes
 * @param {Function} params.goToNextStep - Navigation vers l'étape suivante
 * @param {Function} params.resetSteps - Réinitialisation complète du formulaire
 * @param {Function} params.setErrors - Définit les erreurs de validation affichées
 * @param {Function} params.setValidationFunction - Enregistre la fonction de validation de l'étape courante
 * @param {string} params.currentLanguage - Langue courante pour le payload client
 * @param {Function} [params.recordPaymentError] - Enregistreur d'erreur du signal empreinte (cf. usePaymentSignalTracker)
 * @param {Function} [params.recordPaymentAttempt] - Enregistreur de validation finale avec carte
 * @param {Function} [params.recordCardValidated] - Enregistreur de carte acceptée par Stripe
 * @return {Object} API et état du stepper — `isStepReady`, `setIsStepReady`,
 *     `submitLoading`, `handleComplete`, `handleReset`, `handleChooseAnotherDate`,
 *     `provideValidationFunction`, `handleSetErrors`, `handleNextStep`.
 */
export function useReservationHandlers({
  formData,
  config,
  STEPS,
  isLastStep,
  updateFormData,
  goToStep,
  rewindToStep,
  goToNextStep,
  resetSteps,
  setErrors,
  setValidationFunction,
  currentLanguage,
  recordPaymentError,
  recordPaymentAttempt,
  recordCardValidated,
}) {
  const [isStepReady, setIsStepReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {createReservation, createWaitlistEntry} = useReservationData();
  const submitLoading = isSubmitting;

  const handleComplete = useCallback(() => {
    goToStep(STEPS.CONFIRMATION);
  }, [goToStep, STEPS]);

  const handleReset = useCallback(() => {
    resetSteps();
  }, [resetSteps]);

  const provideValidationFunction = useCallback((validationFn) => {
    setValidationFunction(validationFn);
  }, [setValidationFunction]);

  const handleSetErrors = useCallback((errors) => {
    setErrors(errors);
  }, [setErrors]);

  // Soumission finale : validation Stripe (si requise) puis création API
  const handleSubmitReservation = useCallback(async () => {
    if (!isLastStep) {
      return;
    }

    setIsSubmitting(true);
    // Suivi local : `formData` est figé dans cette closure, la confirmation
    // acquise pendant l'appel n'y serait pas visible dans le catch
    let cardConfirmed = !!formData.confirmedIntent;
    try {
      const dateTimeString = buildReservationDateTime(formData);

      // On n'envoie PAS de `duration` : le backend la résout (durée par défaut du service
      // OU, en mode rotations « 1er/2e service », `releaseBy − arrivée`). L'envoyer ici
      // écraserait la logique de rotation — la table serait occupée pour la durée par
      // défaut au lieu de l'être jusqu'à l'heure de libération.

      // --- Payload de base ---
      const reservationPayload = {
        dateTime: dateTimeString,
        service: formData.service,
        numberOfGuests: formData.partySize,
        // Identifiant du brouillon de parcours : permet au backend de marquer
        // le brouillon converti en post-commit (cf. useReservationDraftSync)
        draftId: formData.draftId,
        customerDetails: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          language: currentLanguage,
          subscribeNewsletter: formData.subscribeNewsletter !== false,
        },
        specialRequests: formData.specialRequests,
        occasion: formData.occasion,
        hasAllergies: formData.hasAllergies || false,
        allergyDetails: formData.allergyDetails || '',
        giftVoucher: {
          hasVoucher: formData.hasGiftVoucher || false,
          code: formData.hasGiftVoucher ? (formData.giftVoucherCode || '').trim() : '',
        },
        source: 'online',
        legalAcceptance: formData.termsAccepted ?
          {termsAcceptedAt: new Date().toISOString()} :
          {},
        // Inclut les formules uniquement si le client en a sélectionné
        ...(formData.selectedFormulas?.length > 0 && {
          formulas: formData.selectedFormulas,
        }),
        // Salle choisie par le client (choix de salle en ligne). Le backend la valide
        // comme réservable puis restreint l'attribution de table à cette salle.
        ...(formData.selectedAreaId && {selectedAreaId: formData.selectedAreaId}),
      };

      // --- Empreinte bancaire ---
      // Le backend renvoie `intentType` (payment|setup) et `captureMethod` (setup_only|preauth)
      // qui déterminent l'appel Stripe.js approprié. Une confirmation déjà acquise par un
      // clic précédent est réutilisée : Stripe refuse de reconfirmer un intent réussi.
      // Bypass : pas d'empreinte bancaire pour les inscriptions liste d'attente
      if (formData.paymentRequired && formData.paymentMethodId && !formData.isWaitlistReservation) {
        if (recordPaymentAttempt) recordPaymentAttempt();
        // Initialise Stripe.js avec le compte Connect si le intent a été créé en direct charges
        const stripe = await getStripePromise(formData.paymentConfig?.stripeAccount);
        const {confirmation, reused} = await confirmCardHold({
          stripe,
          paymentConfig: formData.paymentConfig,
          paymentMethodId: formData.paymentMethodId,
          confirmedIntent: formData.confirmedIntent,
        });
        cardConfirmed = true;
        if (!reused) {
          updateFormData('confirmedIntent', confirmation);
          if (recordCardValidated) recordCardValidated();
        }
        Object.assign(reservationPayload, buildCardHoldPayload(confirmation, formData.paymentConfig));
      }

      // --- Création via l'API ---
      if (formData.isWaitlistReservation) {
        const waitlistPayload = {
          dateTime: dateTimeString,
          service: formData.service,
          numberOfGuests: formData.partySize,
          // Une inscription waitlist finalise aussi le parcours : le backend
          // convertit le brouillon correspondant
          draftId: formData.draftId,
          customerDetails: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            subscribeNewsletter: formData.subscribeNewsletter !== false,
          },
          specialRequests: formData.specialRequests,
          occasion: formData.occasion,
          hasAllergies: formData.hasAllergies || false,
          allergyDetails: formData.allergyDetails || '',
          giftVoucher: {
            hasVoucher: formData.hasGiftVoucher || false,
            code: formData.hasGiftVoucher ? (formData.giftVoucherCode || '').trim() : '',
          },
          source: 'online',
        };

        await createWaitlistEntry(waitlistPayload);
      } else {
        // Le statut réel de la réservation est décidé par le backend : `confirmed`
        // en confirmation automatique, `pending` (demande à valider) quand
        // l'établissement l'a désactivée. On le propage à l'étape de confirmation
        // pour adapter le message, plutôt que d'annoncer « confirmée » en dur.
        const result = await createReservation(reservationPayload);
        const createdStatus = result?.data?.reservation?.status;
        if (createdStatus) {
          updateFormData('reservationStatus', createdStatus);
        }
      }

      setIsSubmitting(false);
      handleComplete();
    } catch (error) {
      setIsSubmitting(false);
      // Trace la raison d'échec sur le brouillon de parcours (journal des non
      // finalisés). Le signal ne qualifie que l'étape empreinte : hors empreinte
      // ou en liste d'attente, un échec de création n'a rien à dire sur la carte.
      if (recordPaymentError && formData.paymentRequired && !formData.isWaitlistReservation) {
        recordPaymentError(error.paymentStage || PAYMENT_ERROR_STAGES.CREATE, error);
      }
      const errorCode = error.response?.data?.code || error.response?.data?.reason;

      // Créneau perdu entre le choix et la validation (service complet, blocage) :
      // écran dédié à la place de l'étape en cours, retour au calendrier proposé.
      // Une inscription en liste d'attente n'a pas de « dernière place » à perdre.
      if (isSlotLostCode(errorCode) && !formData.isWaitlistReservation) {
        updateFormData('slotLost', {code: errorCode, cardValidated: cardConfirmed});
        return;
      }

      // Traduction des codes d'erreur en messages compréhensibles par le client
      let userMessage = 'Une erreur est survenue';

      // Stripe fournit des messages déjà localisés
      if (error.isStripeError) {
        userMessage = error.message;
      // Race condition possible : le créneau a expiré entre la sélection et la soumission
      } else if (errorCode === 'MINIMUM_NOTICE_REQUIRED' || error.message?.includes('Préavis minimum requis')) {
        userMessage = 'Ce créneau n\'est plus disponible (délai de réservation dépassé). Veuillez en choisir un autre.';
      } else if (error.message?.includes('disponibilité') || error.message?.includes('table')) {
        userMessage = 'Ce créneau n\'est plus disponible. Veuillez en choisir un autre.';
      } else if (error.message?.includes('Date invalide') || error.message?.includes('timezone')) {
        userMessage = 'Date ou heure invalide. Vérifiez votre sélection.';
      } else if (error.response?.status === 403) {
        userMessage = 'Accès non autorisé à ce restaurant.';
      } else if (error.response?.status >= 500) {
        userMessage = 'Problème technique temporaire. Veuillez réessayer.';
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      // Les refus métier de la création (mapCreateReservationError) portent
      // leur message sous la clé `error` : sans cette lecture, le client
      // verrait le code HTTP brut d'Axios.
      } else if (typeof error.response?.data?.error === 'string' && error.response.data.error) {
        userMessage = error.response.data.error;
      } else if (error.message) {
        userMessage = error.message;
      }

      setErrors({api: userMessage});
    }
  }, [
    isLastStep,
    formData,
    createReservation,
    createWaitlistEntry,
    updateFormData,
    setErrors,
    handleComplete,
    currentLanguage,
    recordPaymentError,
    recordPaymentAttempt,
    recordCardValidated,
  ]);

  // Créneau perdu : le client repart du calendrier. Service, convives et
  // coordonnées sont conservés ; la date, le créneau et l'empreinte (intent
  // consommé ou obsolète) sont remis à zéro, le préchargement en recréera une.
  const handleChooseAnotherDate = useCallback(() => {
    SLOT_RESET_FIELDS.forEach((field) => updateFormData(field, null));
    setErrors({});
    rewindToStep(STEPS.DATE);
  }, [updateFormData, setErrors, rewindToStep, STEPS]);

  // Avance ou soumet selon la position dans le stepper. L'exigence d'empreinte
  // et le montant sont maintenus à jour en continu par `usePaymentRequirement`
  // (déclenché sur chaque changement de formules sélectionnées), donc aucune
  // synchronisation manuelle n'est nécessaire ici.
  const handleNextStep = useCallback(() => {
    if (isLastStep) {
      handleSubmitReservation();
    } else {
      goToNextStep();
    }
  }, [isLastStep, handleSubmitReservation, goToNextStep]);

  return {
    isStepReady,
    setIsStepReady,
    submitLoading,
    handleComplete,
    handleReset,
    handleChooseAnotherDate,
    provideValidationFunction,
    handleSetErrors,
    handleNextStep,
  };
}
